/* eslint-disable react/require-default-props */

import React from 'react';
import {
  Box,
  Button,
  CardHeader,
  Icon,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { RoleImpact } from '../../../../shared';
import { useInvertedLocalize } from '../../../../utils/localization';

import invertedMessages from './messages';

import './RoleCardHeader.css';

type ImpactLevel = 'low' | 'medium' | 'high';

interface RoleCardHeaderProps {
  userCount: number; // num users matched by this role
  impact?: ImpactLevel;
  onIgnoreButtonClick: () => void;
  isIgnoreButtonDisabled?: boolean;
}

/**
 * RoleCardHeader component displaying the top section of a role card
 * with security icon, user count, impact badge, and menu button
 *
 * @param props - The component props
 * @returns JSX element containing the role card header
 */
function RoleCardHeader({
  userCount,
  impact,
  onIgnoreButtonClick,
  isIgnoreButtonDisabled = false,
}: RoleCardHeaderProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <CardHeader className='Roles-RoleCardHeader'>
      <Box className='Roles-RoleCardHeader-content'>
        {/* Left section: Icon and user count */}
        <Box className='Roles-RoleCardHeader-left'>
          <Icon
            kind='Security'
            size='medium'
            color='--color-foreground-neutral-bold'
          />
          <Typography
            kind='h3'
            color='--color-foreground-neutral-bold'
            className='Roles-RoleCardHeader-userCount'>
            {l(
              '{count, plural, one {# Included User} other {# Included Users}}',
              { count: userCount }
            )}
          </Typography>
          {impact && <RoleImpact impactLevel={impact} iconSize='small' />}
        </Box>

        {/* Right section: Impact badge and menu */}
        <Box className='Roles-RoleCardHeader-right'>
          <Tooltip
            text={
              isIgnoreButtonDisabled
                ? l(
                    "You don't have the permissions to ignore or the feature is turned off."
                  )
                : ''
            }
            isDisabled={!isIgnoreButtonDisabled}
            placement='top'>
            <Button
              kind='ghost'
              size='medium'
              onClick={onIgnoreButtonClick}
              isDisabled={isIgnoreButtonDisabled}
              className='Roles-RoleCardHeader-ignoreButton'>
              {l('Ignore')}
            </Button>
          </Tooltip>
        </Box>
      </Box>
    </CardHeader>
  );
}

export default RoleCardHeader;
